import React from "react";
import { PositionOverlay } from '@src/features/drone/pages/drones-page/components/position-overlay';
import { useSelector } from "react-redux";
import { useFlightProvider } from "@src/features/order/provider";
import { DroneStatusType, useDroneProvider } from "@src/core/features";
import { useGetOrderQuery } from "@src/features/order/redux";
import { skipToken } from "@reduxjs/toolkit/query";
import { useSearchParams } from "react-router-dom";
import { selectSelectedDrone, selectMapLocation, selectMissionId } from '@src/features/drone/redux/selectors';
import { useFlightDroneProvider } from "@src/features/drone/provider/flight-drone-provider/hook";
import { DronesMapProps } from "./types";
import { useDroneSelection } from '@src/features/drone/hooks/useDroneSelection';
import { useFlyingDrones } from '@src/features/drone/hooks/useFlyingDrones';
import { useDroneRotations } from '@src/features/drone/hooks/useDroneRotations';
import { usePrevious } from '@src/features/drone/hooks/usePrevious';
import EnRouteMap from "./en-route";
import { DronesMainMap } from "./main";


const Map: React.FC<DronesMapProps> = (props) => {
  const { goTo, position, removePosition, resetGoHere, points } = props;
  useFlightDroneProvider();
  const { drones: idleDrones } = useDroneProvider(DroneStatusType.IDLE);
  const { drones: missionDrones } = useDroneProvider(DroneStatusType.MISSION);
  const [searchParams] = useSearchParams();
  const location = useSelector(selectMapLocation);
  const selected = useSelector(selectSelectedDrone);
  const missionIdFromRedux = useSelector(selectMissionId);
  const paramsMissionId = searchParams.get('missionId');
  const missionId = missionIdFromRedux || paramsMissionId;
  const { flightData } = useFlightProvider();
  const paramsOrderId = searchParams.get('orderId');
  const orderId = paramsOrderId || selected?.currentOrderId;
  
  const shouldSkipOrderQuery = orderId == null || missionId != null;
  
  const { data: order } = useGetOrderQuery(shouldSkipOrderQuery ? skipToken : orderId, {
    refetchOnFocus: false,
    refetchOnMountOrArgChange: false,
  });
  
  const effectiveMissionId = missionId || order?.missionId;  
  // Manage drone rotations and previous flight positions
  const { rotations, previousFlights } = useDroneRotations(flightData);

  const flyingDrones = useFlyingDrones(missionDrones, previousFlights, flightData);

  const { selectedDrone, isOnMission: isOnMissionFromProvider } = useDroneSelection(selected, flyingDrones, missionDrones, idleDrones);
  // If we have missionId from URL params, consider the drone as on mission
  const isOnMission = isOnMissionFromProvider || !!paramsMissionId;
  const prevIsOnMission = usePrevious(isOnMission);
  // Don't consider it take-off phase if we have missionId from URL params
  const isTakeOff = isOnMissionFromProvider && !orderId && !paramsMissionId;

  React.useEffect(() => {
    if (prevIsOnMission && !isOnMission && selectedDrone) {
      resetGoHere?.();
    }
  }, [isOnMission, prevIsOnMission, selectedDrone, resetGoHere]);

  const displayCreateMission = React.useMemo(() => {
    return location && (selected && !missionDrones.find((drone) => drone.id === selected?.id));
  }, [location, selected, missionDrones]);


  // Show EnRouteMap when:
  // 1. We have effectiveMissionId AND
  // 2. Either: (selectedDrone && isOnMission && !isTakeOff) OR we have missionId from URL params
  const shouldShowEnRouteMap = effectiveMissionId && (
    (selectedDrone && isOnMission && !isTakeOff) || !!paramsMissionId
  );

  return (    
    <React.Fragment>
      {shouldShowEnRouteMap ? 
        <EnRouteMap 
          points={points}
          resetGoHere={resetGoHere} 
          missionId={effectiveMissionId} 
          rotation={selectedDrone ? rotations[selectedDrone.serialNumber] : undefined} 
        />
        : (
          <DronesMainMap
            resetGoHere={resetGoHere}
            position={position}
            removePosition={removePosition}
            points={points}
            goTo={goTo}
          />)}
      
      {position && selectedDrone && (
        <PositionOverlay
          position={position}
          location={location}
          selectedDrone={selected}
          isOnMission={!!isOnMission}
          displayCreateMission={!!displayCreateMission}
          onClose={removePosition}
          goTo={goTo}
          removePosition={removePosition}
        />
      )}
      
    </React.Fragment>
  );
};

export default Map;
